import { useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import Card from '@components/UI/Card';
import Button from '@components/UI/Button';
import { EntitySearchSelect } from '@components/UI';
import { showToast } from '@components/Layout/Toast';
import ScreenerRuleSelect from '@components/UI/ScreenerRuleSelect';
import FormInput from '@components/UI/FormInput';
import FormCheckbox from '@components/UI/FormCheckbox';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import type { EntityTypeUnion } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type { TradingProfileScreenerRuleRow } from '@/types/appRouter';

interface UniverseScreenerSectionProps {
    tradingProfileId: number;
    universeScreenerRuleId: number | null;
    setUniverseScreenerRuleId: Dispatch<SetStateAction<number | null>>;
    isUniverseScreenerActive: boolean;
    setIsUniverseScreenerActive: Dispatch<SetStateAction<boolean>>;
    universeScreenerLink: TradingProfileScreenerRuleRow | null | undefined;
}

const UniverseScreenerSection = ({
    tradingProfileId,
    universeScreenerRuleId,
    setUniverseScreenerRuleId,
    isUniverseScreenerActive,
    setIsUniverseScreenerActive,
    universeScreenerLink
}: UniverseScreenerSectionProps) => {
    const queryClient = useQueryClient();

    const [universeScreenerPreviewEntity, setUniverseScreenerPreviewEntity] = useState<{
        entityId: number;
        entityType: EntityTypeUnion['entityType'];
        symbol?: string;
        name?: string;
    } | null>(null);

    const [universeScreenerPreviewAsOfTimestamp, setUniverseScreenerPreviewAsOfTimestamp] = useState<string>('');

    const [universeScreenerPreviewResult, setUniverseScreenerPreviewResult] = useState<{
        success: boolean;
        qualified: boolean;
        asOfTimestampUsed: number;
        missingDataKeys: string[];
        errorMessages: string[];
    } | null>(null);

    const saveUniverseScreenerLinkMutation = useTrpcMutation(
        trpc.user.setTradingProfileUniverseScreenerLink.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchTradingProfile.queryOptions({ id: tradingProfileId }).queryKey
                });
            }
        }),
        {
            successMessage: 'Universe screener saved successfully',
            fallbackErrorMessage: 'Failed to save universe screener'
        }
    );

    const previewUniverseScreenerMutation = useTrpcMutation(trpc.screener.previewQualify.mutationOptions({}), {
        fallbackErrorMessage: 'Failed to preview screener'
    });

    const handleUniverseScreenerPreview = useCallback(
        async (asOfTimestamp: number) => {
            if (universeScreenerRuleId == null) return;

            if (!universeScreenerPreviewEntity) {
                showToast('Select an entity to preview', 'warning');
                return;
            }

            setUniverseScreenerPreviewResult(null);

            try {
                const result = await previewUniverseScreenerMutation.mutateAsync({
                    screenerRuleId: universeScreenerRuleId,
                    entityId: universeScreenerPreviewEntity.entityId,
                    entityType: universeScreenerPreviewEntity.entityType,
                    asOfTimestamp
                });

                setUniverseScreenerPreviewResult({
                    success: result.success,
                    qualified: result.qualified,
                    asOfTimestampUsed: result.response.asOfTimestampUsed,
                    missingDataKeys: result.response.missingDataKeys,
                    errorMessages: result.errorMessages ?? []
                });
            } catch {
                // Toast handled by useTrpcMutation's fallbackErrorMessage
            }
        },
        [universeScreenerRuleId, universeScreenerPreviewEntity, previewUniverseScreenerMutation]
    );

    const handleSaveUniverseScreener = useCallback(() => {
        if (!tradingProfileId) {
            showToast('Save the trading profile first', 'warning');
            return;
        }

        showSpinner();

        saveUniverseScreenerLinkMutation.mutate(
            {
                tradingProfileId,
                screenerRuleId: universeScreenerRuleId,
                ...(universeScreenerRuleId != null ? { isActive: isUniverseScreenerActive } : {})
            },
            {
                onSuccess: () => {
                    hideSpinner();
                },
                onError: () => {
                    hideSpinner();
                }
            }
        );
    }, [tradingProfileId, universeScreenerRuleId, isUniverseScreenerActive, saveUniverseScreenerLinkMutation]);

    return (
        <Card className="mb-4" title="Universe Screener (Optional)">
            <div className="grid gap-4 md:grid-cols-2">
                <ScreenerRuleSelect
                    helpText="Narrows the deployed strategy's frozen universe (AND with trade filters)."
                    label="Screener Rule"
                    value={universeScreenerRuleId}
                    onChange={(nextScreenerRuleId) => {
                        setUniverseScreenerRuleId(nextScreenerRuleId);
                        setUniverseScreenerPreviewResult(null);
                    }}
                />
                <FormCheckbox
                    checked={isUniverseScreenerActive}
                    disabled={universeScreenerRuleId == null}
                    helpText={universeScreenerRuleId == null ? 'Select a rule to enable.' : undefined}
                    label="Active"
                    onChange={(e) => setIsUniverseScreenerActive(e.target.checked)}
                />
            </div>

            <div className="mt-3 flex items-center gap-3">
                <Button disabled={tradingProfileId <= 0} variant="outline-dark" onClick={handleSaveUniverseScreener}>
                    Save Universe Screener
                </Button>
                {universeScreenerLink ? (
                    <div className="text-sm text-zinc-400">
                        Linked rule: {universeScreenerLink.screenerRuleId} ({universeScreenerLink.isActive === 1 ? 'active' : 'inactive'})
                    </div>
                ) : (
                    <div className="text-sm text-zinc-500">No universe screener linked.</div>
                )}
            </div>

            <div className="mt-5 border-t border-zinc-700 pt-4">
                <div className="mb-2 text-sm font-medium text-zinc-200">Preview</div>
                <div className="grid gap-4 md:grid-cols-2">
                    <EntitySearchSelect
                        disabled={universeScreenerRuleId == null}
                        label="Entity"
                        placeholder={universeScreenerRuleId == null ? 'Select a rule first' : 'Search for an entity...'}
                        value={
                            universeScreenerPreviewEntity
                                ? {
                                      entityId: universeScreenerPreviewEntity.entityId,
                                      entityType: universeScreenerPreviewEntity.entityType
                                  }
                                : null
                        }
                        onChange={(entity) => setUniverseScreenerPreviewEntity(entity)}
                    />
                    <FormInput
                        helpText="Unix timestamp (seconds). Leave blank to use 'now'."
                        id="universeScreenerPreviewAsOfTimestamp"
                        label="As-of timestamp"
                        placeholder="e.g. 1735603200"
                        value={universeScreenerPreviewAsOfTimestamp}
                        onChange={(e) => setUniverseScreenerPreviewAsOfTimestamp(e.target.value)}
                    />
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                        disabled={
                            universeScreenerRuleId == null ||
                            universeScreenerPreviewEntity == null ||
                            previewUniverseScreenerMutation.isPending
                        }
                        variant="outline-dark"
                        onClick={() => handleUniverseScreenerPreview(0)}
                    >
                        {previewUniverseScreenerMutation.isPending ? 'Previewing…' : 'Preview Now'}
                    </Button>

                    <Button
                        disabled={
                            universeScreenerRuleId == null ||
                            universeScreenerPreviewEntity == null ||
                            previewUniverseScreenerMutation.isPending
                        }
                        variant="outline-dark"
                        onClick={() => {
                            const parsed = Number.parseInt(universeScreenerPreviewAsOfTimestamp, 10);

                            if (!Number.isFinite(parsed) || parsed <= 0) {
                                showToast('Enter a valid as-of unix timestamp (seconds)', 'error');
                                return;
                            }

                            void handleUniverseScreenerPreview(parsed);
                        }}
                    >
                        {previewUniverseScreenerMutation.isPending ? 'Previewing…' : 'Preview As-of'}
                    </Button>

                    {universeScreenerPreviewResult ? (
                        <div className="text-sm text-zinc-300">
                            Qualified:{' '}
                            <span className={universeScreenerPreviewResult.qualified ? 'text-emerald-400' : 'text-red-400'}>
                                {universeScreenerPreviewResult.qualified ? 'YES' : 'NO'}
                            </span>
                            {universeScreenerPreviewResult.missingDataKeys.length > 0 ? (
                                <span className="text-zinc-500">
                                    {' '}
                                    (missing: {universeScreenerPreviewResult.missingDataKeys.join(', ')})
                                </span>
                            ) : null}
                            {universeScreenerPreviewResult.errorMessages.length > 0 ? (
                                <div className="mt-1 text-xs text-red-400">
                                    {universeScreenerPreviewResult.errorMessages.map((msg, idx) => (
                                        <div key={idx}>{msg}</div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </Card>
    );
};

export default UniverseScreenerSection;
